import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Select, Collapse } from 'antd';
import { DateTime } from 'luxon';
import { EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

import {
  STATUS_FILTERS,
  DEFAULT_STATUS_FILTERS,
  PRIORITY_FILTERS,
  STATUS_MAP,
  PRIORITY_MAP,
} from './subtask.constants';

import BorderlessButton from '../common/buttons/BorderlessButton';

import SearchFormFilterHeader from '../forms/CompletedForms/SearchFormFilterHeader';

import sortByString, { getIdMap, safeExtractFromObject } from '../helpers/helpers';

import { changeSubtaskStatus } from './state/subtasks.actions';
import { SUBTASK_DUE_DATE_CATEGORIES } from '../helpers/subtasks.constants';
import TableFilterDropdown from '../common/tables/TableFilterDropdown';
import SubtaskPanelHeader from './SubtaskPanelHeader';
import SubtaskPriorityPill from './SubtaskPriorityPill';
import Colors from '../constants/Colors';
import Permissions from '../auth/Permissions';

const { Panel } = Collapse;

const canModifyTask = (record) => (
  Permissions.has('TASKS_OTHERS_WRITE') || record.assigneeId === Permissions.id
);

const getCols = (onStatusChange, statusFilters, showCategory, hasRedesign) => {
  const cols = [{
    title: 'Status',
    dataIndex: 'status',
    sorter: (a, b) => STATUS_MAP[a.status] - STATUS_MAP[b.status],
    showSorterTooltip: false,
    sortDirections: ['descend', 'ascend'],
    filters: STATUS_FILTERS,
    filteredValue: statusFilters,
    onFilter: (value, record) => record.status === value,
    filterDropdown: TableFilterDropdown,
    render: (status, record) => (
      canModifyTask(record) ? (
        <Select
          options={
            STATUS_FILTERS.map((opt) => ({ label: opt.text, value: opt.value }))
          }
          value={status}
          onChange={(newStatus) => onStatusChange(record.id, {
            status: newStatus,
            title: record.title, // For some reason title is required on backend
          })}
          onClick={(e) => e.stopPropagation()}
          dropdownMatchSelectWidth={false}
        />
      ) : status
    ),
  }, {
    title: 'Priority',
    dataIndex: 'priority',
    sorter: (a, b) => PRIORITY_MAP[a.priority] - PRIORITY_MAP[b.priority],
    showSorterTooltip: false,
    sortDirections: ['descend', 'ascend'],
    filters: PRIORITY_FILTERS,
    onFilter: (value, record) => record.priority === value,
    filterDropdown: TableFilterDropdown,
    render: (priority) => (
      <SubtaskPriorityPill priority={priority} />
    ),
  }, {
    title: 'Due Date',
    dataIndex: 'dueDate',
    render: (value, record) => {
      if (!value) return '';
      const now = DateTime.local();
      const dueDateDT = DateTime.fromMillis(value);
      const isOverdue = now >= dueDateDT;
      return (
        <div className="subtask-due-date-container">
          {hasRedesign && (
            <div className="subtask-due-date-icon">
              <ExclamationCircleOutlined style={{ color: Colors.RED_600, fontSize: 13 }} />
            </div>
          )}
          <span style={isOverdue && record.status !== 'Done' ? { color: Colors.RED_600 } : {}}>
            {dueDateDT.toLocaleString(DateTime.DATE_MED)}
          </span>
        </div>
      );
    },
    sorter: (a, b) => a.dueDate - b.dueDate,
    showSorterTooltip: false,
    sortDirections: ['descend', 'ascend'],
  }];

  if (showCategory) {
    cols.push({
      title: 'Category',
      dataIndex: 'category',
      sorter: sortByString('category'),
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    });
  }

  return cols;
};

export default function SubtaskTable({
  subtasks = [],
  onTaskClick,
  onTaskEdit,
  scroll = { y: 'calc(100vh - 300px)' },
  visibleColumns = [],
  defaultHideDone,
  showCategory = false,
}) {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.users.users);
  const settings = useSelector((state) => safeExtractFromObject(state, 'settings.company.settings'));
  const hasRedesign = settings?.uxRedesignPt1;

  const [statusFilters, setStatusFilters] = useState(
    defaultHideDone ? DEFAULT_STATUS_FILTERS : [],
  );
  const [sorterState, setSorterState] = useState(null);

  const [activeKeys, setActiveKeys] = useState([]);

  const onStatusChange = useCallback((id, payload) => {
    dispatch(changeSubtaskStatus(id, payload.status));
  }, []);

  const userIdMap = useMemo(() => getIdMap(users), [users]);
  const userFilters = useMemo(() => {
    const userIdSet = new Set();
    const uFilters = [];
    subtasks.forEach(({ assigneeId }) => {
      if (assigneeId && !userIdSet.has(assigneeId)) {
        userIdSet.add(assigneeId);
        const {
          [assigneeId]: { name } = {},
        } = userIdMap;
        if (!name) return;
        uFilters.push({ text: name, value: assigneeId });
      }
    });
    return uFilters;
  });
  const fullCols = useMemo(() => (
    [{
      title: 'Title',
      dataIndex: 'title',
      sorter: sortByString('title'),
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
      render: (title, record) => (
        <span>
          {title}
          {canModifyTask(record) && (
            <BorderlessButton
              style={{ width: 30, background: 'transparent' }}
              iconNode={<EditOutlined style={{ marginLeft: 0 }} />}
              onClick={(e) => {
                e.stopPropagation();
                onTaskEdit(record.id);
              }}
            />
          )}
        </span>
      ),
    }, {
      title: 'Assigned User',
      dataIndex: 'assigneeId',
      render: (assigneeId) => {
        if (!assigneeId) return null;
        const {
          [assigneeId]: {
            name = '',
          } = {},
        } = userIdMap;
        return name;
      },
      filters: userFilters,
      filterSearch: true,
      onFilter: (value, record) => record.assigneeId === value,
      filterDropdown: SearchFormFilterHeader,
    }].concat(getCols(onStatusChange, statusFilters, showCategory, hasRedesign))
  ), [
    onTaskEdit,
    userFilters,
    userIdMap,
    onStatusChange,
    statusFilters,
    showCategory,
    hasRedesign,
  ]);

  const filteredCols = useMemo(() => {
    if (!visibleColumns.length) return fullCols;
    return fullCols.filter(({ title }) => visibleColumns.includes(title));
  }, [visibleColumns, fullCols]);

  const colsWithSortOrder = useMemo(() => (
    filteredCols.map((col) => {
      const key = col.key ?? col.dataIndex;
      const isSorted = sorterState && (sorterState.columnKey === key || sorterState.field === key);

      return {
        ...col,
        sortOrder: isSorted ? sorterState.order : null,
      };
    })
  ), [filteredCols, sorterState]);

  const subtaskDueDateCategoryMap = useMemo(() => {
    if (!showCategory) return {};
    return subtasks.reduce((acc, subtask) => {
      const { dueDateCategory } = subtask;
      if (!acc[dueDateCategory]) acc[dueDateCategory] = [];
      acc[dueDateCategory].push(subtask);
      return acc;
    }, {});
  }, [subtasks, showCategory]);

  const onHeaderRow = useCallback(() => {
    if (!hasRedesign) return null;
    return {
      className: 'ontraccr-table-header',
    };
  }, [hasRedesign]);

  const TaskTable = useCallback(({ subtaskList }) => {
    let baseRowClassName = 'file-table-row';
    if (hasRedesign) {
      baseRowClassName += ' listview-table-scroll-redesign';
    }

    return (
      <Table
        size="small"
        pagination={false}
        dataSource={subtaskList}
        columns={colsWithSortOrder}
        className={`file-list-container ${showCategory ? 'subtask-page-table' : ''}`}
        rowClassName={baseRowClassName}
        onRow={(record) => ({
          onClick: () => onTaskClick(record),
        })}
        scroll={scroll}
        rowKey="id"
        style={{ height: '100%' }}
        onChange={(_pagination, filters, sorter) => {
          if ('status' in filters) {
            setStatusFilters(filters.status || []);
          }
          const s = Array.isArray(sorter) ? sorter[0] : sorter;
          setSorterState(s && s.order ? s : null);
        }}
        onHeaderRow={onHeaderRow}
      />
    );
  }, [colsWithSortOrder, onTaskClick, scroll, showCategory, hasRedesign, onHeaderRow]);

  const defaultActiveKeys = useMemo(() => (
    Object.values(SUBTASK_DUE_DATE_CATEGORIES).filter((category) => (
      subtaskDueDateCategoryMap[category]?.length
    ))
  ), [subtaskDueDateCategoryMap]);

  useEffect(() => {
    setActiveKeys(defaultActiveKeys);
  }, [defaultActiveKeys]);

  if (!showCategory) {
    return <TaskTable subtaskList={subtasks} />;
  }

  return (
    <Collapse
      activeKey={activeKeys}
      className={hasRedesign ? 'subtask-collapse-redesign' : 'subtask-collapse'}
      ghost
      onChange={setActiveKeys}
    >
      {Object.values(SUBTASK_DUE_DATE_CATEGORIES).map((category) => (
        <Panel
          header={(
            <SubtaskPanelHeader
              title={category}
              count={subtaskDueDateCategoryMap[category]?.length}
            />
          )}
          key={category}
        >
          <TaskTable subtaskList={subtaskDueDateCategoryMap[category]} />
        </Panel>
      ))}
    </Collapse>
  );
}

/* eslint-disable react/forbid-prop-types */
SubtaskTable.propTypes = {
  subtasks: PropTypes.array,
  onTaskClick: PropTypes.func.isRequired,
  onTaskEdit: PropTypes.func.isRequired,
  scroll: PropTypes.object,
  visibleColumns: PropTypes.array,
  defaultHideDone: PropTypes.bool,
  showCategory: PropTypes.bool,
};

SubtaskTable.defaultProps = {
  subtasks: [],
  scroll: { y: 'calc(100vh - 300px)' },
  visibleColumns: [],
  defaultHideDone: true,
  showCategory: false,
};
